import { WebGLContext } from 'features/assetDatabase/contexts/WebGLLayer'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoUnit,
} from 'types'

import { ManagedAssetViz } from './ManagedAssetViz'

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
`

interface AssetImageGeneratorProps {
  unit: GeckoUnit
  assetModel: GeckoAssetModel | undefined
  component: GeckoComponent | undefined
  getColorThresholdForComponent?: (
    cdf: GeckoComponentDataFrame
  ) => GeckoColorScaleThreshold | undefined
  componentDataFrame: GeckoComponentDataFrame | undefined
  assetVizID: string //This must be unique for each instance of AssetImageGenerator
}

export const AssetImageGenerator: React.FC<AssetImageGeneratorProps> = (
  props
) => {
  const {
    assetVizID,
    assetModel,
    component,
    getColorThresholdForComponent,
    componentDataFrame,
  } = props
  const { setAssetVizRenderingMap, assetVizRenderingMap } =
    useContext(WebGLContext)
  const { theme } = useGeckoTheme()
  const [assetImage, setAssetImage] = useState<string | null>(null)
  const [showFallback, setShowFallback] = useState<boolean>(false)

  useEffect(() => {
    if (assetImage && assetVizRenderingMap.has(assetVizID)) {
      const updatedMap = new Map(assetVizRenderingMap)
      updatedMap.delete(assetVizID)
      setAssetVizRenderingMap(updatedMap)
    }
  }, [assetImage, assetVizRenderingMap, setAssetVizRenderingMap, assetVizID])

  const assetVizEntry = useMemo(() => {
    return component && componentDataFrame
      ? {
          assetColor: theme.forecast.secondaryText,
          assetModel: assetModel,
          getColorThresholdForComponent: getColorThresholdForComponent,
          components: [component],
          selectedCDF: componentDataFrame,
        }
      : undefined
  }, [
    component,
    componentDataFrame,
    theme.forecast.secondaryText,
    assetModel,
    getColorThresholdForComponent,
  ])

  useEffect(() => {
    if (assetVizEntry) {
      const timer = setTimeout(() => {
        if (!assetImage) {
          setShowFallback(true)
        }
      }, 8000)
      return () => clearTimeout(timer)
    }
  }, [assetVizEntry, assetImage])

  return (
    <>
      {!assetImage && assetVizEntry && !showFallback ? (
        <ManagedAssetViz
          id={assetVizID}
          assetModel={assetVizEntry.assetModel}
          components={assetVizEntry.components}
          getColorThresholdForComponent={
            assetVizEntry.getColorThresholdForComponent
          }
          componentDataFrame={assetVizEntry.selectedCDF}
          assetColor={assetVizEntry.assetColor}
          setAssetImage={setAssetImage}
        />
      ) : assetImage ? (
        <StyledImage src={assetImage} id={`${assetVizID}-image`} alt='' />
      ) : (
        <></>
      )}
    </>
  )
}
